import { forwardRef, useCallback, useMemo, useState } from "react";
import RSelect from "react-select";
import classNames from "classnames";

import { CFormGroup, CInput, CLabel } from "@coreui/react";

import { formatSearchString } from "src/utils/funcs";

const selectTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: "#053C7F",
  },
});

function Select(
  {
    label,
    onChange,
    className,
    value,
    options,
    ignore,
    display = "label",
    select,
    disabled,
    isDisabled,
    optionAll = false,
    onCreate,
    isOptionDisabled,
    onChangeCallback,
    ...rest
  },
  ref,
) {
  const _class = classNames("c-input c-select", className);

  const [input, setInput] = useState("");

  const _options = useMemo(() => {
    const result =
      options?.filter(
        (o) => !ignore?.includes(o.value) || (value && o.value === value),
      ) ?? [];
    if (optionAll) {
      return [{ value: "", label: "Tất cả", [display]: "Tất cả" }, ...result];
    } else {
      return result;
    }
  }, [options, ignore, optionAll, display]);

  const _value = useMemo(() => {
    if (typeof value === "object" && value !== null) {
      return (
        _options?.find((v) => v[select || "value"] === value?.value) || null
      );
    }

    return _options?.find((v) => v[select || "value"] === value) || null;
  }, [value, _options, select]);

  const change = useCallback(
    (v) => {
      if (select) {
        onChange?.(v[select], v);
        onChangeCallback?.(v[select], v);
      } else {
        onChange?.(v);
        onChangeCallback?.(v);
      }
    },
    [onChange, select, onChangeCallback],
  );

  return (
    <CFormGroup className={_class}>
      {label && (
        <CLabel>
          {label} {rest.required && <span className="text-danger">*</span>}
        </CLabel>
      )}
      {rest.readOnly ? (
        <CInput
          className="c-input"
          value={(display ? _value?.[display] : _value?.label) ?? ""}
          {...rest}
        />
      ) : (
        <RSelect
          ref={ref}
          className={!!rest.invalid && "is-invalid"}
          value={_value}
          options={_options || []}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          menuShouldScrollIntoView={true}
          theme={selectTheme}
          onChange={change}
          classNamePrefix="react-select"
          isDisabled={disabled || isDisabled}
          getOptionLabel={display ? (option) => option[display] : undefined}
          isOptionDisabled={isOptionDisabled}
          filterOption={(option, inputValue) => {
            return formatSearchString(
              option[display] || option["label"] || option["name"],
            )?.includes(formatSearchString(inputValue));
          }}
          inputValue={input}
          onInputChange={(newInputValue) => setInput(newInputValue)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const isExist = options.some((e) =>
                formatSearchString(
                  e[display] || e["label"] || e["name"],
                )?.includes(formatSearchString(input)),
              );
              if (!isExist) {
                onCreate?.(input);
              }
            }
          }}
          {...rest}
        />
      )}
    </CFormGroup>
  );
}

export default forwardRef(Select);
